import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { cached, remember } from "@/lib/cache";
import TimeGrid from "@/components/calendar/TimeGrid";
import MonthGrid from "@/components/calendar/MonthGrid";
import BookingLinks from "@/components/calendar/BookingLinks";
import { colourEvents } from "@/components/calendar/colours";
import ColourRules from "@/components/calendar/ColourRules";
import { useAuth } from "@/context/AuthContext";
import { ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";

const DEFAULT_OWNER = "vedant@thickandthin.media";
const INTERNAL = /scrum|check[\s-]?in|day[\s-]?end/i;
const VIEWS = [["day", "Day"], ["week", "Week"], ["month", "Month"]];
const dayStart = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function rangeFor(view, anchor) {
  if (view === "day") return [anchor, 1];
  if (view === "week") return [addDays(anchor, -anchor.getDay()), 7];
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  return [addDays(first, -first.getDay()), 42];
}

function title(view, anchor, start) {
  if (view === "day") return anchor.toLocaleDateString([], { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  if (view === "month") return anchor.toLocaleDateString([], { month: "long", year: "numeric" });
  const end = addDays(start, 6);
  return start.getMonth() === end.getMonth() ? start.toLocaleDateString([], { month: "long", year: "numeric" })
    : `${start.toLocaleDateString([], { month: "short" })} – ${end.toLocaleDateString([], { month: "short", year: "numeric" })}`;
}

function Toolbar({ view, anchor, start, owner, users, set, step, onRefresh, hide, setHide, hiddenCount }) {
  return (
    <div className="flex flex-wrap items-center gap-2 md:gap-3 px-4 md:px-6 py-3 border-b border-[var(--border)]">
      <button className="btn text-xs rounded-full px-4" onClick={() => set({ date: ymd(new Date()) })} data-testid="calendar-today">Today</button>
      <div className="flex items-center">
        <button className="p-2 rounded-full hover:bg-[var(--panel-2)]" onClick={() => step(-1)} data-testid="calendar-prev"><ChevronLeft size={18} /></button>
        <button className="p-2 rounded-full hover:bg-[var(--panel-2)]" onClick={() => step(1)} data-testid="calendar-next"><ChevronRight size={18} /></button>
      </div>
      <h1 className="text-lg md:text-xl font-normal tracking-tight mr-auto" data-testid="calendar-title">{title(view, anchor, start)}</h1>
      <label className="flex items-center gap-2 text-xs text-[var(--text-dim)] cursor-pointer select-none" title="Hides Scrum, Check-in and Day End meetings" data-testid="calendar-hide-internal">
        <input type="checkbox" checked={hide} onChange={(e) => setHide(e.target.checked)} data-testid="calendar-hide-internal-toggle" />
        Hide internal{hide && hiddenCount > 0 ? ` (${hiddenCount})` : ""}
      </label>
      <button className="p-2 rounded-full hover:bg-[var(--panel-2)]" onClick={onRefresh} data-testid="calendar-refresh"><RefreshCw size={14} /></button>
      <select value={owner} onChange={(e) => set({ owner: e.target.value })} className="btn text-xs bg-transparent rounded-full" data-testid="calendar-owner-select">
        {users.map((u) => <option key={u.email} value={u.email} className="bg-black">{u.name || u.email}</option>)}
      </select>
      <div className="flex rounded-full border border-[var(--border)] overflow-hidden" data-testid="calendar-views">
        {VIEWS.map(([k, l]) => <button key={k} onClick={() => set({ view: k })} data-testid={`view-${k}`} className={`px-3 py-1.5 text-xs transition-colors ${view === k ? "bg-[#8ab4f8]/20 text-[#8ab4f8]" : "hover:bg-[var(--panel-2)]"}`}>{l}</button>)}
      </div>
    </div>
  );
}

function Legend({ mode, setMode, legend, counts, hidden, setHidden, rulesEditor }) {
  const toggle = (k) => setHidden(hidden.includes(k) ? hidden.filter((x) => x !== k) : [...hidden, k]);
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px] text-neutral-400 px-1" data-testid="calendar-legend">
      <div className="flex rounded-full border border-[var(--border)] overflow-hidden mr-1" data-testid="calendar-colour-mode">
        {[["outcome", "Show-up"], ["stage", "Lead stage"], ["qualified", "Qualified"]].map(([k, l]) => (
          <button key={k} onClick={() => setMode(k)} data-testid={`colour-mode-${k}`}
            className={`px-2.5 py-1 transition-colors ${mode === k ? "bg-[#8ab4f8]/20 text-[#8ab4f8]" : "hover:bg-[var(--panel-2)]"}`}>{l}</button>
        ))}
      </div>
      {rulesEditor}
      {legend.map(([k, l, c]) => (
        <label key={k} className={`flex items-center gap-1.5 cursor-pointer select-none transition-opacity ${hidden.includes(k) ? "opacity-40" : ""}`} data-testid={`legend-${k.replace(/\s+/g, "-").toLowerCase()}`}>
          <input type="checkbox" className="sr-only" checked={!hidden.includes(k)} onChange={() => toggle(k)} data-testid={`legend-toggle-${k.replace(/\s+/g, "-").toLowerCase()}`} />
          <i className="w-2.5 h-2.5 rounded-sm border" style={{ background: hidden.includes(k) ? "transparent" : c, borderColor: c }} />
          {l}<span className="text-neutral-600">{counts[k] || 0}</span>
        </label>
      ))}
      {hidden.length > 0 && <button className="underline text-neutral-500" onClick={() => setHidden([])} data-testid="legend-show-all">Show all</button>}
    </div>
  );
}

export default function CalendarPage() {
  const [params, setParams] = useSearchParams();
  const view = VIEWS.some((v) => v[0] === params.get("view")) ? params.get("view") : (window.innerWidth < 768 ? "day" : "week");
  const owner = params.get("owner") || DEFAULT_OWNER;
  const anchor = params.get("date") ? dayStart(new Date(`${params.get("date")}T00:00:00`)) : dayStart(new Date());
  const [start, len] = rangeFor(view, anchor);
  const end = addDays(start, len);
  const [users, setUsers] = useState(cached("cal_users") || []);
  const [data, setData] = useState(null);
  const [tick, setTick] = useState(0);
  const [hide, setHideState] = useState(localStorage.getItem("cal_hide_internal") === "1");
  const setHide = (v) => { localStorage.setItem("cal_hide_internal", v ? "1" : "0"); setHideState(v); };
  const set = (patch) => setParams((p) => { const n = new URLSearchParams(p); Object.entries(patch).forEach(([k, v]) => n.set(k, v)); return n; }, { replace: true });
  const step = (dir) => {
    if (view === "month") set({ date: ymd(new Date(anchor.getFullYear(), anchor.getMonth() + dir, 1)) });
    else set({ date: ymd(addDays(anchor, dir * len)) });
  };

  useEffect(() => { api.get("/crm/users").then((r) => setUsers(remember("cal_users", r.data.rows.filter((u) => !/\(api\)/i.test(u.name || ""))))); }, []);
  useEffect(() => {
    let live = true;
    const key = `cal:${owner}:${start.toISOString()}:${len}`;
    setData(cached(key) || null);
    api.get("/calendar/events", { params: { owner, start: start.toISOString(), end: end.toISOString() } })
      .then((r) => { if (live) setData(remember(key, r.data)); }).catch(() => { if (live) setData({ events: [], leads: {}, error: true }); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [owner, start.getTime(), len, tick]);

  const days = Array.from({ length: len }, (_, i) => addDays(start, i));
  useEffect(() => {
    const bump = () => setData((d) => (d ? { ...d, leads: { ...d.leads } } : d));
    window.addEventListener("cal-leads-changed", bump);
    return () => window.removeEventListener("cal-leads-changed", bump);
  }, []);
  const name = users.find((u) => u.email === owner)?.name || owner;
  const all = data?.events || [];
  const visible = hide ? all.filter((ev) => !INTERNAL.test(ev.title || "")) : all;
  const hiddenCount = all.length - visible.length;
  const [mode, setModeState] = useState(localStorage.getItem("cal_colour_mode") || "outcome");
  const [hiddenCats, setHiddenState] = useState(() => { try { return JSON.parse(localStorage.getItem(`cal_hidden_${mode}`)) || []; } catch { return []; } });
  const setMode = (m) => { localStorage.setItem("cal_colour_mode", m); setModeState(m); try { setHiddenState(JSON.parse(localStorage.getItem(`cal_hidden_${m}`)) || []); } catch { setHiddenState([]); } };
  const setHiddenCats = (h) => { localStorage.setItem(`cal_hidden_${mode}`, JSON.stringify(h)); setHiddenState(h); };
  const [rules, setRules] = useState(cached("cal_colour_rules") || {});
  useEffect(() => { api.get("/crm/config").then((r) => setRules(remember("cal_colour_rules", r.data.calendar_colours || {}))); }, []);
  const { events, legend, counts } = colourEvents(visible, data?.leads || {}, mode, hiddenCats, rules);
  const { user } = useAuth();
  const tab = params.get("tab") === "links" && user?.role === "admin" ? "links" : "calendar";
  const tabs = user?.role === "admin" && (
    <div className="flex gap-4 px-3 md:px-5 pt-3" data-testid="calendar-tabs">
      {[["calendar", "Calendar"], ["links", "Booking links"]].map(([k, l]) => (
        <button key={k} className={`text-sm pb-1 ${tab === k ? "font-medium border-b-2 border-white" : "text-neutral-500"}`} onClick={() => set({ tab: k })} data-testid={`calendar-tab-${k}`}>{l}</button>
      ))}
    </div>
  );
  if (tab === "links") return <div data-testid="calendar-page">{tabs}<BookingLinks /></div>;

  return (
    <div data-testid="calendar-page" className="overflow-x-hidden">
      {tabs}
      <Toolbar {...{ view, anchor, start, owner, users, set, step, hide, setHide, hiddenCount }} onRefresh={() => setTick((t) => t + 1)} />
      <div className="p-2 md:p-4 space-y-3">
        <div className="flex items-center gap-3 text-[11px] text-neutral-500 px-1">
          <span data-testid="calendar-summary">{name}'s calendar · {data ? `${events.length} event${events.length === 1 ? "" : "s"}` : "loading…"}</span>
        </div>
        <Legend mode={mode} setMode={setMode} legend={legend} counts={counts} hidden={hiddenCats} setHidden={setHiddenCats}
          rulesEditor={user?.role === "admin" && mode !== "stage" && <ColourRules rules={rules} onSaved={(r) => setRules(remember("cal_colour_rules", r))} />} />
        {data && !data.connected && (
          <div className="panel-2 px-4 py-3 text-xs text-amber-400" data-testid="calendar-not-connected">
            {name}'s Google Calendar isn't connected, so only meetings booked through your forms show. Connect it in Settings → Google accounts.
          </div>
        )}
        <div className={data ? "" : "opacity-50 pointer-events-none transition-opacity"}>
          {view === "month"
            ? <MonthGrid days={days} month={anchor.getMonth()} events={events} leads={data?.leads || {}} onDay={(d) => set({ view: "day", date: ymd(d) })} />
            : <TimeGrid days={days} events={events} leads={data?.leads || {}} />}
        </div>
      </div>
    </div>
  );
}
